import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../api';
import { dateTimeLocalValue, zonedDateTimeToIso } from '../time';
import type { Campaign, CampaignRole, InventoryAccount, Location, Run, Wave } from '../types';
import {
  EmptyState,
  ErrorNotice,
  Loading,
  Section,
  StatusPill,
  formString,
  formatNzDateTime,
  numberValue,
  stringValue,
} from '../components/Common';

function CampaignSetup({ campaign, role }: { campaign: Campaign; role: CampaignRole }) {
  const queryClient = useQueryClient();
  const [confirmed, setConfirmed] = useState(false);
  const initialize = useMutation({
    mutationFn: (quantity: number) => apiClient.initializeInventory(campaign.id, quantity),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
        queryClient.invalidateQueries({ queryKey: ['inventory'] }),
      ]);
    },
  });
  const update = useMutation({
    mutationFn: (input: Record<string, unknown>) => apiClient.updateCampaign(campaign.id, input),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
        queryClient.invalidateQueries({ queryKey: ['campaign'] }),
      ]);
    },
  });
  const create = useMutation({
    mutationFn: apiClient.createCampaign,
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
  });
  if (role !== 'admin') return null;
  return (
    <>
      {campaign.inventorySetupRequired ? (
        <Section title="Confirm printed flyer stock" className="priority-panel">
          <div className="notice notice-warning">
            <strong>No quantity has been assumed</strong>
            <span>
              Enter the exact printed quantity once. Future changes become auditable inventory
              adjustments.
            </span>
          </div>
          {initialize.error ? <ErrorNotice error={initialize.error} /> : null}
          <form
            className="inline-form"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              initialize.mutate(Number(form.get('quantity')));
            }}
          >
            <label>
              Exact flyers printed
              <input name="quantity" type="number" min={1} max={10_000_000} required />
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
              />
              I have checked this against the print order.
            </label>
            <button className="button button-primary" disabled={!confirmed || initialize.isPending}>
              {initialize.isPending ? 'Confirming…' : 'Confirm initial stock'}
            </button>
          </form>
        </Section>
      ) : null}
      <Section title="Campaign settings">
        {update.error ? <ErrorNotice error={update.error} /> : null}
        {update.isSuccess ? (
          <div className="notice notice-success" role="status">
            Settings saved.
          </div>
        ) : null}
        <form
          className="form-grid"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            const timezone = formString(form, 'timezone');
            update.mutate({
              name: form.get('name'),
              publicLabel: form.get('publicLabel'),
              campaignLine: form.get('campaignLine'),
              timezone,
              startDate: form.get('startDate') || null,
              endDate: form.get('endDate') || null,
              operationalDeadline: form.get('operationalDeadline')
                ? zonedDateTimeToIso(formString(form, 'operationalDeadline'), timezone)
                : null,
              status: form.get('status'),
              defaultRevisitIntervalDays: Number(form.get('revisitDays')),
              branding: {
                primaryColour: form.get('primaryColour'),
                accentColour: form.get('accentColour'),
                highlightColour: form.get('highlightColour'),
                backgroundColour: form.get('backgroundColour'),
                textColour: form.get('textColour'),
              },
            });
          }}
        >
          <label className="field-wide">
            Campaign name
            <input name="name" defaultValue={campaign.name} required />
          </label>
          <label className="field-wide">
            Public label
            <input name="publicLabel" defaultValue={campaign.publicLabel} required />
          </label>
          <label className="field-wide">
            Campaign line
            <input name="campaignLine" defaultValue={campaign.campaignLine} />
          </label>
          <label>
            Timezone
            <input name="timezone" defaultValue={campaign.timezone} required />
          </label>
          <label>
            Status
            <select name="status" defaultValue={campaign.status}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="archived">Archived</option>
            </select>
          </label>
          <label>
            Start date
            <input name="startDate" type="date" defaultValue={campaign.startDate ?? ''} />
          </label>
          <label>
            End date
            <input name="endDate" type="date" defaultValue={campaign.endDate ?? ''} />
          </label>
          <label>
            Operational deadline
            <input
              name="operationalDeadline"
              type="datetime-local"
              defaultValue={dateTimeLocalValue(campaign.operationalDeadline, campaign.timezone)}
            />
          </label>
          <label>
            Default revisit days
            <input
              name="revisitDays"
              type="number"
              min={1}
              max={365}
              defaultValue={campaign.defaultRevisitIntervalDays}
              required
            />
          </label>
          <fieldset className="field-wide">
            <legend>Campaign colours</legend>
            <div className="form-grid">
              <label>
                Primary
                <input
                  name="primaryColour"
                  type="color"
                  defaultValue={campaign.branding.primaryColour}
                />
              </label>
              <label>
                Accent
                <input
                  name="accentColour"
                  type="color"
                  defaultValue={campaign.branding.accentColour}
                />
              </label>
              <label>
                Highlight
                <input
                  name="highlightColour"
                  type="color"
                  defaultValue={campaign.branding.highlightColour}
                />
              </label>
              <label>
                Background
                <input
                  name="backgroundColour"
                  type="color"
                  defaultValue={campaign.branding.backgroundColour}
                />
              </label>
              <label>
                Text
                <input name="textColour" type="color" defaultValue={campaign.branding.textColour} />
              </label>
            </div>
          </fieldset>
          <button className="button button-primary" disabled={update.isPending}>
            Save settings
          </button>
        </form>
      </Section>
      <Section title="Campaign portfolio">
        <p className="page-intro">
          This installation can hold several campaigns. New campaigns start empty, with no assumed
          stock.
        </p>
        {create.error ? <ErrorNotice error={create.error} /> : null}
        {create.isSuccess ? (
          <div className="notice notice-success" role="status">
            Campaign created. Select it from the campaign menu to finish setup.
          </div>
        ) : null}
        <details className="details-form">
          <summary>Create another campaign</summary>
          <form
            className="form-grid"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              const timezone = formString(form, 'timezone');
              create.mutate({
                name: form.get('name'),
                publicLabel: form.get('publicLabel'),
                campaignLine: form.get('campaignLine'),
                timezone,
                startDate: form.get('startDate') || null,
                endDate: form.get('endDate') || null,
                operationalDeadline: form.get('operationalDeadline')
                  ? zonedDateTimeToIso(formString(form, 'operationalDeadline'), timezone)
                  : null,
                defaultRevisitIntervalDays: Number(form.get('revisitDays')),
                branding: {
                  primaryColour: form.get('primaryColour'),
                  accentColour: form.get('accentColour'),
                  highlightColour: form.get('highlightColour'),
                  backgroundColour: form.get('backgroundColour'),
                  textColour: form.get('textColour'),
                },
              });
            }}
          >
            <label className="field-wide">
              Campaign name
              <input name="name" required />
            </label>
            <label className="field-wide">
              Public label
              <input name="publicLabel" required />
            </label>
            <label className="field-wide">
              Campaign line
              <input name="campaignLine" />
            </label>
            <label>
              Timezone
              <input name="timezone" defaultValue="Pacific/Auckland" required />
            </label>
            <label>
              Default revisit days
              <input name="revisitDays" type="number" min={1} max={365} defaultValue={7} required />
            </label>
            <label>
              Start date
              <input name="startDate" type="date" />
            </label>
            <label>
              End date
              <input name="endDate" type="date" />
            </label>
            <label>
              Operational deadline
              <input name="operationalDeadline" type="datetime-local" />
            </label>
            <fieldset className="field-wide">
              <legend>Campaign colours</legend>
              <div className="form-grid">
                <label>
                  Primary
                  <input
                    name="primaryColour"
                    type="color"
                    defaultValue={campaign.branding.primaryColour}
                  />
                </label>
                <label>
                  Accent
                  <input
                    name="accentColour"
                    type="color"
                    defaultValue={campaign.branding.accentColour}
                  />
                </label>
                <label>
                  Highlight
                  <input
                    name="highlightColour"
                    type="color"
                    defaultValue={campaign.branding.highlightColour}
                  />
                </label>
                <label>
                  Background
                  <input
                    name="backgroundColour"
                    type="color"
                    defaultValue={campaign.branding.backgroundColour}
                  />
                </label>
                <label>
                  Text
                  <input
                    name="textColour"
                    type="color"
                    defaultValue={campaign.branding.textColour}
                  />
                </label>
              </div>
            </fieldset>
            <button className="button button-primary" disabled={create.isPending}>
              Create campaign
            </button>
          </form>
        </details>
      </Section>
    </>
  );
}

function LocationReview({
  campaign,
  role,
  waves,
  runs,
}: {
  campaign: Campaign;
  role: CampaignRole;
  waves: Wave[];
  runs: Run[];
}) {
  const queryClient = useQueryClient();
  const [campusId, setCampusId] = useState('');
  const [buildingId, setBuildingId] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [historyLocationId, setHistoryLocationId] = useState('');
  const [showOnlyUnreviewed, setShowOnlyUnreviewed] = useState(true);
  const dashboard = useQuery({
    queryKey: ['dashboard', campaign.id, 'admin-review'],
    queryFn: () => apiClient.dashboard(campaign.id),
  });
  const campuses = dashboard.data?.data.campuses ?? [];
  const buildings = (dashboard.data?.data.buildings ?? []).filter(
    (building) => !campusId || stringValue(building.campus_id) === campusId,
  );
  useEffect(() => {
    if (!campusId && campuses[0]) setCampusId(stringValue(campuses[0].id));
  }, [campusId, campuses]);
  useEffect(() => {
    const valid = buildings.some((building) => stringValue(building.id) === buildingId);
    if (!valid) setBuildingId(stringValue(buildings[0]?.id));
  }, [buildingId, buildings]);
  const locations = useQuery({
    queryKey: ['locations', 'admin-review', campaign.id, buildingId, showOnlyUnreviewed],
    enabled: Boolean(buildingId),
    queryFn: () =>
      apiClient.allLocations({
        campaignId: campaign.id,
        buildingId,
        permissionStatus: showOnlyUnreviewed ? 'unreviewed' : undefined,
        status: 'all',
        active: 'all',
      }),
  });
  const history = useQuery({
    queryKey: ['location-history', historyLocationId],
    enabled: Boolean(historyLocationId),
    queryFn: () => apiClient.location(historyLocationId),
  });
  const correction = useMutation({
    mutationFn: ({
      originalEventId,
      waveId,
      quantity,
      notes,
    }: {
      originalEventId: string;
      waveId: string;
      quantity: number;
      notes: string;
    }) =>
      apiClient.recordVisit({
        id: crypto.randomUUID(),
        idempotencyKey: crypto.randomUUID(),
        campaignId: campaign.id,
        locationId: historyLocationId,
        waveId,
        runId: null,
        clientOccurredAt: new Date().toISOString(),
        flyerQuantity: quantity,
        actionType: 'correction',
        notes,
        accessOutcome: 'not_needed',
        participantUserIds: [],
        correctionOfEventId: originalEventId,
        source: 'online',
        clientAppVersion: '1.0.0',
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['location-history', historyLocationId] }),
        queryClient.invalidateQueries({ queryKey: ['locations'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
        queryClient.invalidateQueries({ queryKey: ['inventory'] }),
        queryClient.invalidateQueries({ queryKey: ['inventory-ledger'] }),
      ]);
    },
  });
  const invalidate = async () => {
    setSelected([]);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['locations'] }),
      queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
    ]);
  };
  const bulk = useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      apiClient.bulkUpdateLocations({ campaignId: campaign.id, locationIds: selected, ...input }),
    onSuccess: invalidate,
  });
  const plan = useMutation({
    mutationFn: ({
      waveId,
      targetQuantity,
      revisitIntervalDays,
    }: {
      waveId: string;
      targetQuantity: number;
      revisitIntervalDays: number;
    }) =>
      apiClient.bulkWavePlan(waveId, {
        campaignId: campaign.id,
        locationIds: selected,
        targetQuantity,
        revisitIntervalDays,
        state: 'planned',
      }),
    onSuccess: invalidate,
  });
  const assign = useMutation({
    mutationFn: (runId: string) => apiClient.assignRunLocations(runId, selected),
    onSuccess: invalidate,
  });
  const rows = locations.data?.data ?? [];
  const allSelected = rows.length > 0 && rows.every((location) => selected.includes(location.id));
  return (
    <Section
      title="Location review and planning"
      action={<StatusPill tone="warning">{locations.data?.pagination.total ?? 0} shown</StatusPill>}
    >
      <div className="notice notice-safety">
        <strong>Review before distribution</strong>
        <span>
          Imported spaces start unreviewed. Approval must reflect real permission; never approve a
          locked, restricted, staff-only, or otherwise unauthorized space.
        </span>
      </div>
      <div className="review-controls">
        <label>
          Campus
          <select
            value={campusId}
            onChange={(event) => {
              setCampusId(event.target.value);
              setBuildingId('');
              setSelected([]);
            }}
          >
            {campuses.map((campus) => (
              <option value={stringValue(campus.id)} key={stringValue(campus.id)}>
                {stringValue(campus.name)}
              </option>
            ))}
          </select>
        </label>
        <label>
          Building
          <select
            value={buildingId}
            onChange={(event) => {
              setBuildingId(event.target.value);
              setSelected([]);
            }}
          >
            {buildings.map((building) => (
              <option value={stringValue(building.id)} key={stringValue(building.id)}>
                {stringValue(building.name)}
              </option>
            ))}
          </select>
        </label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={showOnlyUnreviewed}
            onChange={(event) => {
              setShowOnlyUnreviewed(event.target.checked);
              setSelected([]);
            }}
          />
          Unreviewed only
        </label>
        <button
          className="button"
          type="button"
          onClick={() =>
            window.open(
              `/print?campaignId=${encodeURIComponent(campaign.id)}&buildingId=${encodeURIComponent(buildingId)}`,
              '_blank',
              'noopener',
            )
          }
          disabled={!buildingId}
        >
          Print checklist
        </button>
      </div>
      {locations.isLoading ? <Loading label="Loading building…" /> : null}
      {locations.error ? <ErrorNotice error={locations.error} /> : null}
      <div className="bulk-toolbar">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={(event) =>
              setSelected(event.target.checked ? rows.map((location) => location.id) : [])
            }
          />
          Select all ({selected.length})
        </label>
        {role === 'admin' ? (
          <>
            <button
              className="button button-primary"
              type="button"
              disabled={selected.length === 0 || bulk.isPending}
              onClick={() =>
                bulk.mutate({
                  permissionStatus: 'approved',
                  accessStatus: 'accessible',
                  distributionSuitability: 'suitable',
                })
              }
            >
              Approve selected
            </button>
            <button
              className="button"
              type="button"
              disabled={selected.length === 0 || bulk.isPending}
              onClick={() =>
                bulk.mutate({
                  permissionStatus: 'do_not_distribute',
                  distributionSuitability: 'unsuitable',
                })
              }
            >
              Do not distribute
            </button>
          </>
        ) : null}
      </div>
      {bulk.error || plan.error || assign.error ? (
        <ErrorNotice error={bulk.error ?? plan.error ?? assign.error} />
      ) : null}
      <div className="review-list">
        {rows.map((location: Location) => (
          <div className="review-row" key={location.id}>
            <input
              type="checkbox"
              aria-label={`Select ${location.roomCode}`}
              checked={selected.includes(location.id)}
              onChange={(event) =>
                setSelected((current) =>
                  event.target.checked
                    ? [...current, location.id]
                    : current.filter((id) => id !== location.id),
                )
              }
            />
            <span>
              <strong>{location.roomCode}</strong>
              <small>
                {location.locationType}
                {location.capacity ? ` · ${location.capacity} seats` : ''}
              </small>
            </span>
            <StatusPill tone={location.permissionStatus === 'approved' ? 'success' : 'warning'}>
              {location.permissionStatus.replaceAll('_', ' ')}
            </StatusPill>
            <button
              className="button button-small"
              type="button"
              onClick={() => setHistoryLocationId(location.id)}
            >
              History
            </button>
          </div>
        ))}
        {!locations.isLoading && rows.length === 0 ? (
          <EmptyState title="No rooms to review">
            Change the filter or choose another building.
          </EmptyState>
        ) : null}
      </div>
      {historyLocationId ? (
        <div className="panel">
          <div className="section-heading">
            <h3>
              {history.data?.data.roomCode
                ? `${history.data.data.roomCode} history`
                : 'Room history'}
            </h3>
            <button
              className="button button-small"
              type="button"
              onClick={() => setHistoryLocationId('')}
            >
              Close
            </button>
          </div>
          {history.isLoading ? <Loading label="Loading room history…" /> : null}
          {history.error ? <ErrorNotice error={history.error} /> : null}
          {correction.error ? <ErrorNotice error={correction.error} /> : null}
          {history.data?.data.history.length ? (
            <ol className="timeline">
              {history.data.data.history.map((visit) => (
                <li key={stringValue(visit.id)}>
                  <span className="timeline-dot" aria-hidden="true" />
                  <div>
                    <strong>{stringValue(visit.action_type).replaceAll('_', ' ')}</strong>
                    <span>
                      {stringValue(visit.recording_volunteer)} · {stringValue(visit.participants)} ·{' '}
                      {formatNzDateTime(stringValue(visit.server_received_at))}
                    </span>
                    {['drop', 'correction'].includes(stringValue(visit.action_type)) ? (
                      <details className="details-form">
                        <summary>Correct this event</summary>
                        <form
                          className="inline-form"
                          onSubmit={(event) => {
                            event.preventDefault();
                            const form = new FormData(event.currentTarget);
                            correction.mutate({
                              originalEventId: stringValue(visit.id),
                              waveId: stringValue(visit.wave_id),
                              quantity: Number(form.get('quantity')),
                              notes: formString(form, 'notes'),
                            });
                          }}
                        >
                          <label>
                            Signed flyer adjustment
                            <input
                              name="quantity"
                              type="number"
                              min={-10_000}
                              max={10_000}
                              placeholder="-2"
                              required
                            />
                          </label>
                          <label>
                            Reason
                            <input name="notes" minLength={3} required />
                          </label>
                          <button className="button" disabled={correction.isPending}>
                            Record correction
                          </button>
                        </form>
                      </details>
                    ) : null}
                  </div>
                  <strong>{numberValue(visit.flyer_quantity)}</strong>
                </li>
              ))}
            </ol>
          ) : history.isLoading ? null : (
            <EmptyState title="No room history">No visits have been recorded here yet.</EmptyState>
          )}
        </div>
      ) : null}
      <div className="bulk-planning">
        <form
          className="inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            bulk.mutate({
              priority: Number(form.get('priority')),
              defaultTargetQuantity: Number(form.get('target')),
              defaultRevisitIntervalDays: Number(form.get('revisit')),
            });
          }}
        >
          <strong>Bulk defaults</strong>
          <label>
            Priority
            <input name="priority" type="number" min={1} max={5} defaultValue={3} />
          </label>
          <label>
            Target
            <input name="target" type="number" min={0} max={10_000} defaultValue={5} />
          </label>
          <label>
            Revisit days
            <input name="revisit" type="number" min={1} max={365} defaultValue={7} />
          </label>
          <button className="button" disabled={selected.length === 0}>
            Update selected
          </button>
        </form>
        <form
          className="inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            plan.mutate({
              waveId: formString(form, 'waveId'),
              targetQuantity: Number(form.get('target')),
              revisitIntervalDays: Number(form.get('revisit')),
            });
          }}
        >
          <strong>Assign to wave</strong>
          <label>
            Wave
            <select name="waveId">
              {waves.map((wave) => (
                <option value={wave.id} key={wave.id}>
                  {wave.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Target
            <input name="target" type="number" min={0} defaultValue={5} />
          </label>
          <label>
            Revisit days
            <input name="revisit" type="number" min={1} defaultValue={7} />
          </label>
          <button className="button" disabled={selected.length === 0}>
            Plan selected
          </button>
        </form>
        <form
          className="inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            assign.mutate(formString(new FormData(event.currentTarget), 'runId'));
          }}
        >
          <strong>Assign to run</strong>
          <label>
            Run
            <select name="runId">
              {runs
                .filter((run) => ['planned', 'active', 'paused'].includes(run.status))
                .map((run) => (
                  <option value={run.id} key={run.id}>
                    {run.name}
                  </option>
                ))}
            </select>
          </label>
          <button className="button" disabled={selected.length === 0 || runs.length === 0}>
            Assign selected
          </button>
        </form>
      </div>
    </Section>
  );
}

function WaveAdmin({ campaign, waves }: { campaign: Campaign; waves: Wave[] }) {
  const queryClient = useQueryClient();
  const refresh = async () => queryClient.invalidateQueries({ queryKey: ['waves'] });
  const create = useMutation({
    mutationFn: apiClient.createWave,
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: ({ waveId, input }: { waveId: string; input: Record<string, unknown> }) =>
      apiClient.updateWave(waveId, input),
    onSuccess: refresh,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    create.mutate({
      campaignId: campaign.id,
      name: form.get('name'),
      description: form.get('description') || undefined,
      startsAt: zonedDateTimeToIso(formString(form, 'startsAt'), campaign.timezone),
      endsAt: zonedDateTimeToIso(formString(form, 'endsAt'), campaign.timezone),
      status: 'planned',
      sortOrder: waves.length + 1,
    });
  };
  return (
    <Section title="Distribution waves">
      <div className="compact-card-list">
        {waves.map((wave) => (
          <article key={wave.id}>
            <div>
              <strong>{wave.name}</strong>
              <span>
                {formatNzDateTime(wave.startsAt)} – {formatNzDateTime(wave.endsAt)}
              </span>
            </div>
            <StatusPill tone={wave.status === 'active' ? 'success' : 'neutral'}>
              {wave.completedLocations}/{wave.plannedLocations}
            </StatusPill>
          </article>
        ))}
      </div>
      {create.error || update.error ? <ErrorNotice error={create.error ?? update.error} /> : null}
      {waves.length ? (
        <details className="details-form">
          <summary>Edit waves and dates</summary>
          <div className="compact-card-list">
            {waves.map((wave) => (
              <form
                className="form-grid panel"
                key={`edit-${wave.id}`}
                onSubmit={(event) => {
                  event.preventDefault();
                  const form = new FormData(event.currentTarget);
                  update.mutate({
                    waveId: wave.id,
                    input: {
                      name: form.get('name'),
                      description: form.get('description') || undefined,
                      startsAt: zonedDateTimeToIso(formString(form, 'startsAt'), campaign.timezone),
                      endsAt: zonedDateTimeToIso(formString(form, 'endsAt'), campaign.timezone),
                      status: form.get('status'),
                      sortOrder: Number(form.get('sortOrder')),
                    },
                  });
                }}
              >
                <strong>{wave.name}</strong>
                <label className="field-wide">
                  Name
                  <input name="name" defaultValue={wave.name} required />
                </label>
                <label>
                  Starts
                  <input
                    name="startsAt"
                    type="datetime-local"
                    defaultValue={dateTimeLocalValue(wave.startsAt, campaign.timezone)}
                    required
                  />
                </label>
                <label>
                  Ends
                  <input
                    name="endsAt"
                    type="datetime-local"
                    defaultValue={dateTimeLocalValue(wave.endsAt, campaign.timezone)}
                    required
                  />
                </label>
                <label>
                  Status
                  <select name="status" defaultValue={wave.status}>
                    <option value="planned">Planned</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </label>
                <label>
                  Route order
                  <input
                    name="sortOrder"
                    type="number"
                    min={0}
                    max={1000}
                    defaultValue={wave.sortOrder}
                    required
                  />
                </label>
                <label className="field-wide">
                  Description
                  <textarea name="description" defaultValue={wave.description ?? ''} rows={2} />
                </label>
                <button className="button" disabled={update.isPending}>
                  Save wave
                </button>
              </form>
            ))}
          </div>
        </details>
      ) : null}
      <details className="details-form">
        <summary>Create a wave</summary>
        <form className="form-grid" onSubmit={submit}>
          <label className="field-wide">
            Name
            <input name="name" required />
          </label>
          <label>
            Starts
            <input name="startsAt" type="datetime-local" required />
          </label>
          <label>
            Ends
            <input name="endsAt" type="datetime-local" required />
          </label>
          <label className="field-wide">
            Description
            <textarea name="description" rows={2} />
          </label>
          <button className="button button-primary" disabled={create.isPending}>
            Create wave
          </button>
        </form>
      </details>
    </Section>
  );
}

function UserAdmin({ campaign }: { campaign: Campaign }) {
  const queryClient = useQueryClient();
  const [inviteLink, setInviteLink] = useState('');
  const refresh = async () => queryClient.invalidateQueries({ queryKey: ['users'] });
  const users = useQuery({
    queryKey: ['users', campaign.id],
    queryFn: () => apiClient.users(campaign.id),
  });
  const create = useMutation({
    mutationFn: apiClient.createUser,
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: ({ userId, input }: { userId: string; input: Record<string, unknown> }) =>
      apiClient.updateUser(userId, input),
    onSuccess: refresh,
  });
  const resetPassword = useMutation({
    mutationFn: ({ userId, password }: { userId: string; password: string }) =>
      apiClient.resetUserPassword(userId, { campaignId: campaign.id, password }),
    onSuccess: refresh,
  });
  const invite = useMutation({
    mutationFn: apiClient.createUserInvite,
    onSuccess: (response) => {
      setInviteLink(`${location.origin}/?invite=${encodeURIComponent(response.data.token)}`);
    },
  });
  return (
    <Section title="Users and roles">
      {users.isLoading ? <Loading label="Loading users…" /> : null}
      {users.error ? <ErrorNotice error={users.error} /> : null}
      <div className="compact-card-list">
        {users.data?.data.map((user) => (
          <article key={stringValue(user.id)}>
            <div>
              <strong>{stringValue(user.displayName)}</strong>
              <span>
                @{stringValue(user.username)} · {stringValue(user.role)}
              </span>
            </div>
            <StatusPill tone={user.disabled ? 'danger' : 'success'}>
              {user.disabled ? 'disabled' : `${numberValue(user.stockBalance)} flyers`}
            </StatusPill>
          </article>
        ))}
      </div>
      {create.error || update.error || resetPassword.error || invite.error ? (
        <ErrorNotice error={create.error ?? update.error ?? resetPassword.error ?? invite.error} />
      ) : null}
      {users.data?.data.length ? (
        <details className="details-form">
          <summary>Manage existing users</summary>
          <div className="compact-card-list">
            {users.data.data.map((user) => {
              const userId = stringValue(user.id);
              const disabled = Boolean(user.disabled);
              return (
                <div className="panel" key={`manage-${userId}`}>
                  <strong>{stringValue(user.displayName)}</strong>
                  <form
                    className="inline-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      const form = new FormData(event.currentTarget);
                      update.mutate({
                        userId,
                        input: { campaignId: campaign.id, role: form.get('role') },
                      });
                    }}
                  >
                    <label>
                      Role
                      <select name="role" defaultValue={stringValue(user.role)}>
                        <option value="volunteer">Volunteer</option>
                        <option value="viewer">Viewer</option>
                        <option value="coordinator">Coordinator</option>
                        <option value="admin">Admin</option>
                      </select>
                    </label>
                    <button className="button" disabled={update.isPending}>
                      Save role
                    </button>
                    <button
                      className={disabled ? 'button' : 'button button-danger-text'}
                      type="button"
                      disabled={update.isPending}
                      onClick={() =>
                        update.mutate({
                          userId,
                          input: { campaignId: campaign.id, disabled: !disabled },
                        })
                      }
                    >
                      {disabled ? 'Enable account' : 'Disable account'}
                    </button>
                  </form>
                  <form
                    className="inline-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      const form = new FormData(event.currentTarget);
                      resetPassword.mutate({
                        userId,
                        password: formString(form, 'password'),
                      });
                    }}
                  >
                    <label>
                      New temporary password
                      <input name="password" type="password" minLength={12} required />
                    </label>
                    <button className="button" disabled={resetPassword.isPending}>
                      Reset password and sessions
                    </button>
                  </form>
                </div>
              );
            })}
          </div>
        </details>
      ) : null}
      <details className="details-form">
        <summary>Create a user</summary>
        <form
          className="form-grid"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            create.mutate({
              campaignId: campaign.id,
              displayName: form.get('displayName'),
              username: form.get('username'),
              email: form.get('email') || undefined,
              password: form.get('password'),
              role: form.get('role'),
            });
          }}
        >
          <label>
            Display name
            <input name="displayName" required />
          </label>
          <label>
            Username
            <input name="username" minLength={3} required />
          </label>
          <label>
            Email <span className="optional">(optional)</span>
            <input name="email" type="email" />
          </label>
          <label>
            Role
            <select name="role" defaultValue="volunteer">
              <option value="volunteer">Volunteer</option>
              <option value="viewer">Viewer</option>
              <option value="coordinator">Coordinator</option>
              <option value="admin">Admin</option>
            </select>
          </label>
          <label className="field-wide">
            Temporary password
            <input name="password" type="password" minLength={12} required />
          </label>
          <button className="button button-primary" disabled={create.isPending}>
            Create user
          </button>
        </form>
      </details>
      <details className="details-form">
        <summary>Create one-time invitation</summary>
        <form
          className="form-grid"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            invite.mutate({
              campaignId: campaign.id,
              username: form.get('username'),
              email: form.get('email') || undefined,
              role: form.get('role'),
              expiresInHours: Number(form.get('expiresInHours')),
            });
          }}
        >
          <label>
            Reserved username
            <input name="username" minLength={3} required />
          </label>
          <label>
            Email restriction <span className="optional">(optional)</span>
            <input name="email" type="email" />
          </label>
          <label>
            Role
            <select name="role" defaultValue="volunteer">
              <option value="volunteer">Volunteer</option>
              <option value="viewer">Viewer</option>
              <option value="coordinator">Coordinator</option>
              <option value="admin">Admin</option>
            </select>
          </label>
          <label>
            Expires in hours
            <input
              name="expiresInHours"
              type="number"
              min={1}
              max={168}
              defaultValue={72}
              required
            />
          </label>
          <button className="button" disabled={invite.isPending}>
            Create invitation
          </button>
        </form>
        {inviteLink ? (
          <div className="notice notice-warning" role="status">
            <strong>Copy this link now; the token is shown only once.</strong>
            <input aria-label="One-time invitation link" value={inviteLink} readOnly />
          </div>
        ) : null}
      </details>
    </Section>
  );
}

function InventoryAdmin({
  campaign,
  accounts,
  role,
}: {
  campaign: Campaign;
  accounts: InventoryAccount[];
  role: CampaignRole;
}) {
  const queryClient = useQueryClient();
  const ledger = useQuery({
    queryKey: ['inventory-ledger', campaign.id],
    queryFn: () => apiClient.inventoryLedger(campaign.id),
  });
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['inventory'] }),
      queryClient.invalidateQueries({ queryKey: ['inventory-ledger'] }),
      queryClient.invalidateQueries({ queryKey: ['runs'] }),
      queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
    ]);
  };
  const transfer = useMutation({
    mutationFn: apiClient.transferInventory,
    onSuccess: refresh,
  });
  const adjustment = useMutation({
    mutationFn: apiClient.adjustInventory,
    onSuccess: refresh,
  });
  return (
    <Section title="Inventory ledger">
      <div className="inventory-account-grid">
        {accounts.map((account) => (
          <article key={account.id}>
            <span>{account.label}</span>
            <strong>{account.balance}</strong>
            <small>{account.accountType}</small>
          </article>
        ))}
      </div>
      {transfer.error || adjustment.error ? (
        <ErrorNotice error={transfer.error ?? adjustment.error} />
      ) : null}
      <form
        className="inline-form"
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          transfer.mutate({
            campaignId: campaign.id,
            fromAccountId: form.get('from'),
            toAccountId: form.get('to'),
            quantity: Number(form.get('quantity')),
            occurredAt: new Date().toISOString(),
            notes: form.get('notes') || undefined,
            idempotencyKey: crypto.randomUUID(),
          });
        }}
      >
        <strong>Issue or return stock</strong>
        <label>
          From
          <select name="from">
            {accounts.map((account) => (
              <option value={account.id} key={account.id}>
                {account.label} ({account.balance})
              </option>
            ))}
          </select>
        </label>
        <label>
          To
          <select name="to">
            {accounts.map((account) => (
              <option value={account.id} key={account.id}>
                {account.label} ({account.balance})
              </option>
            ))}
          </select>
        </label>
        <label>
          Quantity
          <input name="quantity" type="number" min={1} required />
        </label>
        <label>
          Note
          <input name="notes" />
        </label>
        <button className="button button-primary" disabled={transfer.isPending}>
          Record transfer
        </button>
      </form>
      {role === 'admin' ? (
        <details className="details-form">
          <summary>Administrative adjustment</summary>
          <form
            className="inline-form"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              adjustment.mutate({
                campaignId: campaign.id,
                accountId: form.get('account'),
                movementType: form.get('movementType'),
                quantityDelta: Number(form.get('quantityDelta')),
                reason: form.get('reason'),
                occurredAt: new Date().toISOString(),
                idempotencyKey: crypto.randomUUID(),
              });
            }}
          >
            <label>
              Account
              <select name="account">
                {accounts.map((account) => (
                  <option value={account.id} key={account.id}>
                    {account.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Movement type
              <select name="movementType" defaultValue="adjustment">
                <option value="adjustment">Administrative adjustment</option>
                <option value="damaged">Damaged flyers</option>
                <option value="lost">Lost flyers</option>
              </select>
            </label>
            <label>
              Signed quantity
              <input name="quantityDelta" type="number" required />
            </label>
            <label>
              Reason
              <input name="reason" minLength={3} required />
            </label>
            <button className="button">Record adjustment</button>
          </form>
        </details>
      ) : null}
      <div className="ledger-list">
        {ledger.data?.data.slice(0, 20).map((movement) => (
          <article key={stringValue(movement.id)}>
            <div>
              <strong>{stringValue(movement.movement_type).replaceAll('_', ' ')}</strong>
              <span>
                {stringValue(movement.from_account) || 'External'} →{' '}
                {stringValue(movement.to_account) || 'Distributed / removed'}
              </span>
              <small>
                {stringValue(movement.recorded_by)} ·{' '}
                {formatNzDateTime(stringValue(movement.server_recorded_at))}
              </small>
            </div>
            <strong>{numberValue(movement.quantity)}</strong>
          </article>
        ))}
      </div>
    </Section>
  );
}

function ImportAndExport({ campaign }: { campaign: Campaign }) {
  const [detailId, setDetailId] = useState('');
  const imports = useQuery({
    queryKey: ['imports', campaign.id],
    queryFn: () => apiClient.imports(campaign.id),
  });
  const detail = useQuery({
    queryKey: ['import-detail', detailId],
    queryFn: () => apiClient.importDetail(detailId),
    enabled: Boolean(detailId),
  });
  const selectedReport = detail.data?.data.report as Record<string, unknown> | undefined;
  const conflicts = (detail.data?.data.conflicts ?? []) as Array<Record<string, unknown>>;
  return (
    <Section title="Imports, exports, and fallback checklists">
      <div className="export-actions">
        <a
          className="button"
          href={`/api/v1/exports/locations.csv?campaignId=${encodeURIComponent(campaign.id)}`}
        >
          Export locations CSV
        </a>
        <a
          className="button"
          href={`/api/v1/exports/visits.csv?campaignId=${encodeURIComponent(campaign.id)}`}
        >
          Export visits CSV
        </a>
        <a
          className="button button-primary"
          href={`/api/v1/exports/backup.json?campaignId=${encodeURIComponent(campaign.id)}`}
        >
          Download JSON backup
        </a>
        <a
          className="button"
          href={`/api/v1/exports/backup.json?campaignId=${encodeURIComponent(campaign.id)}&archive=1`}
        >
          Archive JSON to R2
        </a>
      </div>
      <div className="compact-card-list">
        {imports.data?.data.map((batch) => (
          <button
            className="import-row"
            type="button"
            key={stringValue(batch.id)}
            onClick={() => setDetailId(stringValue(batch.id))}
          >
            <span>
              <strong>{stringValue(batch.source_filename)}</strong>
              <small>{stringValue(batch.source_sha256)}</small>
            </span>
            <StatusPill tone="success">{numberValue(batch.imported_record_count)} rooms</StatusPill>
          </button>
        ))}
      </div>
      {detail.isLoading ? <Loading label="Loading import report…" /> : null}
      {detail.error ? <ErrorNotice error={detail.error} /> : null}
      {selectedReport ? (
        <div className="import-report">
          <h3>Import report</h3>
          <dl className="run-facts">
            <div>
              <dt>Imported</dt>
              <dd>{numberValue(selectedReport.importedRecordCount)}</dd>
            </div>
            <div>
              <dt>Conflicts preserved</dt>
              <dd>{numberValue(selectedReport.conflictCount)}</dd>
            </div>
            <div>
              <dt>Canonical buildings</dt>
              <dd>{numberValue(selectedReport.canonicalBuildingCount)}</dd>
            </div>
            <div>
              <dt>Source labels</dt>
              <dd>{numberValue(selectedReport.sourceBuildingLabelCount)}</dd>
            </div>
          </dl>
          <pre>{JSON.stringify(selectedReport.campusTotals, null, 2)}</pre>
          <details>
            <summary>Conflicts ({conflicts.length} loaded)</summary>
            <div className="conflict-list">
              {conflicts.map((conflict) => (
                <article key={stringValue(conflict.id)}>
                  <strong>
                    {stringValue(conflict.normalized_room_key)} · {stringValue(conflict.field_name)}
                  </strong>
                  <span>{stringValue(conflict.resolution)}</span>
                </article>
              ))}
            </div>
          </details>
        </div>
      ) : null}
    </Section>
  );
}

export function AdminPage({ campaign, role }: { campaign: Campaign; role: CampaignRole }) {
  const waves = useQuery({
    queryKey: ['waves', campaign.id],
    queryFn: () => apiClient.waves(campaign.id),
  });
  const runs = useQuery({
    queryKey: ['runs', campaign.id],
    queryFn: () => apiClient.runs(campaign.id),
  });
  const accounts = useQuery({
    queryKey: ['inventory', campaign.id],
    queryFn: () => apiClient.inventoryAccounts(campaign.id),
  });
  if (waves.isLoading || runs.isLoading || accounts.isLoading) {
    return <Loading label="Loading operations tools…" />;
  }
  if (waves.error || runs.error || accounts.error) {
    return <ErrorNotice error={waves.error ?? runs.error ?? accounts.error} />;
  }
  return (
    <div className="page admin-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">{role === 'admin' ? 'Administration' : 'Coordination'}</p>
          <h1>Operations control</h1>
        </div>
        <StatusPill tone="info">{role}</StatusPill>
      </header>
      <CampaignSetup campaign={campaign} role={role} />
      <LocationReview
        campaign={campaign}
        role={role}
        waves={waves.data?.data ?? []}
        runs={runs.data?.data ?? []}
      />
      <div className="admin-columns">
        <WaveAdmin campaign={campaign} waves={waves.data?.data ?? []} />
        {role === 'admin' ? <UserAdmin campaign={campaign} /> : null}
      </div>
      <InventoryAdmin campaign={campaign} accounts={accounts.data?.data ?? []} role={role} />
      {role === 'admin' ? <ImportAndExport campaign={campaign} /> : null}
    </div>
  );
}
